import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { graphTrpc } from '@org/clients/graph';
import { Avatar, AvatarFallback, AvatarImage, Button, Skeleton, Tabs, TabsList, TabsTrigger, TabsContent } from '@org/ui';
import { Settings, Trophy, Calendar, MapPin, Star, TrendingUp, MessageCircle, Heart, FileText, Users, CheckCircle, Zap } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

export const Route = createFileRoute('/profile')({
  component: ProfilePage,
});

const ACTION_DISPLAY: Record<string, { label: string; icon: typeof Star; color: string }> = {
  post_created: { label: 'activityTypes.createdPost', icon: FileText, color: 'text-blue-500' },
  comment_created: { label: 'activityTypes.commented', icon: MessageCircle, color: 'text-green-500' },
  like_received: { label: 'activityTypes.receivedLike', icon: Heart, color: 'text-red-500' },
  post_pinned: { label: 'activityTypes.postPinned', icon: Star, color: 'text-amber-500' },
  space_joined: { label: 'activityTypes.joinedSpace', icon: Users, color: 'text-purple-500' },
  first_post_bonus: { label: 'activityTypes.firstPostBonus', icon: Zap, color: 'text-amber-500' },
  lesson_completed: { label: 'activityTypes.completedLesson', icon: CheckCircle, color: 'text-green-500' },
  class_attended: { label: 'activityTypes.attendedClass', icon: Calendar, color: 'text-blue-500' },
  homework_completed: { label: 'activityTypes.completedHomework', icon: CheckCircle, color: 'text-green-500' },
  daily_login: { label: 'activityTypes.dailyLogin', icon: TrendingUp, color: 'text-primary' },
};

function ProfilePage() {
  const { t } = useTranslation('community');

  const { data: profile, isLoading } = graphTrpc.profiles.getMyProfile.useQuery();

  const { data: myRankData } = graphTrpc.points.getMyRank.useQuery();
  const { data: levelThresholds } = graphTrpc.points.getLevelThresholds.useQuery();
  const { data: pointsHistory } = graphTrpc.points.getHistory.useQuery({ limit: 20 });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
            <Skeleton className="h-24 w-24 rounded-full" />
            <div className="flex-1 space-y-2 text-center sm:text-left">
              <Skeleton className="mx-auto h-6 w-48 sm:mx-0" />
              <Skeleton className="mx-auto h-4 w-32 sm:mx-0" />
              <Skeleton className="mx-auto h-4 w-64 sm:mx-0" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="rounded-2xl border border-border bg-card p-8 text-center">
        <p className="text-muted-foreground">{t('profile.notFound')}</p>
      </div>
    );
  }

  const displayName = profile.displayName ||
    [profile.firstName, profile.lastName].filter(Boolean).join(' ') ||
    'Community Member';
  const initial = displayName.slice(0, 1).toUpperCase();

  const points = myRankData?.points ?? profile.points ?? 0;
  const levelInfo = myRankData?.level;
  const level = levelInfo?.level ?? 1;
  const levelName = levelInfo?.name ?? 'New Member';
  const nextLevelAt = levelInfo?.nextLevelAt;

  const currentLevelThreshold = levelThresholds?.levels?.find(l => l.level === level);
  const currentLevelAt = currentLevelThreshold?.minPoints ?? 0;
  const progressPercent = nextLevelAt
    ? Math.min(100, ((points - currentLevelAt) / (nextLevelAt - currentLevelAt)) * 100)
    : 100;

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
          <Avatar className="h-24 w-24">
            <AvatarImage src={profile.profileImageUrl ?? undefined} />
            <AvatarFallback className="bg-primary/10 text-2xl text-primary">
              {initial}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 text-center sm:text-left">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl font-bold">{displayName}</h1>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {t('profile.level')} {level} - {levelName}
                  </span>
                  {myRankData?.rank && (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      {t('profile.rank', { rank: myRankData.rank })}
                    </span>
                  )}
                </div>
              </div>
              <Link to="/settings">
                <Button variant="outline" size="sm" className="gap-2">
                  <Settings className="h-4 w-4" aria-hidden="true" />
                  {t('profile.edit')}
                </Button>
              </Link>
            </div>

            {/* Level Progress Bar */}
            {nextLevelAt && (
              <div className="mt-3">
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                  <span>{points} / {nextLevelAt} pts</span>
                  <span>{t('profile.ptsToNext', { points: nextLevelAt - points, level: level + 1 })}</span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={Math.round(progressPercent)} aria-valuemin={0} aria-valuemax={100}>
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            )}

            {profile.bio && (
              <p className="mt-3 text-sm text-muted-foreground">{profile.bio}</p>
            )}

            <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground sm:justify-start">
              {profile.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" aria-hidden="true" />
                  {profile.location}
                </span>
              )}
              {profile.createdAt && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" aria-hidden="true" />
                  {t('profile.joinedOn')} {format(new Date(profile.createdAt), 'MMM yyyy')}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 gap-4 border-t border-border pt-6 sm:grid-cols-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-primary">{points}</p>
            <p className="text-sm text-muted-foreground">{t('profile.points')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold">{profile.postsCount ?? 0}</p>
            <p className="text-sm text-muted-foreground">{t('profile.posts')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold">{profile.followersCount ?? 0}</p>
            <p className="text-sm text-muted-foreground">{t('profile.followers')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold">{profile.followingCount ?? 0}</p>
            <p className="text-sm text-muted-foreground">{t('profile.following')}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="posts">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="posts">{t('profile.posts')}</TabsTrigger>
          <TabsTrigger value="activity" className="flex items-center gap-1">
            <TrendingUp className="h-4 w-4" aria-hidden="true" />
            {t('profile.activity')}
          </TabsTrigger>
          <TabsTrigger value="badges">{t('profile.badges')}</TabsTrigger>
        </TabsList>

        <TabsContent value="posts" className="mt-4">
          <div className="rounded-2xl border border-border bg-card p-8 text-center">
            <p className="text-muted-foreground">{t('profile.noPosts')}</p>
          </div>
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <div className="rounded-2xl border border-border bg-card">
            {pointsHistory?.transactions && pointsHistory.transactions.length > 0 ? (
              <div className="divide-y divide-border">
                {pointsHistory.transactions.map((tx) => {
                  const actionInfo = ACTION_DISPLAY[tx.actionType] ?? {
                    label: tx.actionType,
                    icon: Star,
                    color: 'text-muted-foreground',
                  };
                  const ActionIcon = actionInfo.icon;

                  return (
                    <div key={tx.transactionId} className="flex items-center gap-4 p-4">
                      <div className={`flex h-10 w-10 items-center justify-center rounded-full bg-muted ${actionInfo.color}`}>
                        <ActionIcon className="h-5 w-5" aria-hidden="true" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{t(actionInfo.label)}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(tx.createdAt), { addSuffix: true })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-green-600">+{tx.pointsAwarded}</p>
                        <p className="text-xs text-muted-foreground">{t('profile.points').toLowerCase()}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-8 text-center">
                <TrendingUp className="mx-auto h-12 w-12 text-muted-foreground" aria-hidden="true" />
                <p className="mt-4 text-muted-foreground">{t('profile.noActivity')}</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="badges" className="mt-4">
          <div className="rounded-2xl border border-border bg-card p-8 text-center">
            <Trophy className="mx-auto h-12 w-12 text-muted-foreground" aria-hidden="true" />
            <p className="mt-4 text-muted-foreground">{t('profile.noBadges')}</p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
